import {
  AxisUnit,
  CreateChartOptions,
  EventsConfig,
  isIntradayResample,
} from '@chart';
import {
  flip,
  offset,
  shift,
  useClientPoint,
  useFloating,
} from '@floating-ui/react';
import { config } from '@terminal/config/app.config';
import { useChartApi } from '@terminal/hooks/use-chart-api/useChartApi';
import { useDomainChangeEventListener } from '@terminal/hooks/use-domain-change-event-listener/useDomainChangeEventListener';
import { TooltipSortConfig } from '@terminal/modules/multi-line-chart/MultiLineChartModule';
import { useGutterSize } from '@terminal/modules/turbo-chart/hooks/use-gutter-size/useGutterSize';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { Insights } from '@terminal/views/turbo-chart/components/insights/Insights';
import { useChartInsights } from '@terminal/views/turbo-chart/hooks/use-chart-insights/useChartInsights';
import { useLegendPriceHover } from '@terminal/views/turbo-chart/hooks/use-legend-price-hover/useLegendPriceHover';
import { UseTurboChartReturn } from '@terminal/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import type {
  TerminalChartAssetData,
  TerminalPaneData,
} from '@terminal/views/turbo-chart/utils/chart-utils/types';
import { RefObject, useMemo } from 'react';

import { SimpleChartTooltip } from '../simple-chart-tooltip/SimpleChartTooltip';

/** (avoids y-axis overflow). */
const INSIGHT_RIGHT_AXIS_BUFFER_PX = 8;

export interface ChartAreaProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  enablePriceHover?: boolean;
  options: CreateChartOptions;
  chartState: UseTurboChartReturn['chartState'];
  primaryAsset: TerminalChartAssetData;
  primaryPane: TerminalPaneData;
  onDomainChange?: (event: Event) => void;
  tooltipSortConfig?: TooltipSortConfig;
  axisUnit?: string;
  displayAxisUnit?: boolean;
  eventsConfig?: EventsConfig;
  source: PosthogTrackingSource;
}

export const ChartArea = ({
  canvasRef,
  enablePriceHover = true,
  options,
  chartState,
  primaryAsset,
  primaryPane,
  onDomainChange,
  tooltipSortConfig,
  axisUnit,
  displayAxisUnit = true,
  eventsConfig = {
    TMI: false,
  },
  source,
}: ChartAreaProps) => {
  const chartApiRef = useChartApi({
    canvasRef,
    options,
    chartState,
    primaryAsset,
    primaryPane,
  });

  useDomainChangeEventListener({ canvasRef, onDomainChange });

  const { gutterSize } = useGutterSize({
    canvasRef,
  });

  const priceHover = useLegendPriceHover({
    elementRef: canvasRef,
    enabled: enablePriceHover,
  });

  const { refs, floatingStyles, context } = useFloating({
    open: !!priceHover,
    placement: 'top',
    strategy: 'fixed',
    middleware: [offset(15), flip(), shift({ padding: 5 })],
  });
  useClientPoint(context, {
    x: priceHover?.x,
    y: priceHover?.y,
  });

  const chartData = useMemo(
    () => chartState.chartPanes.flatMap(p => p.chartAssetData),
    [chartState.chartPanes]
  );
  const { insightsLoading, insights } = useChartInsights({
    chartData,
    eventsConfig,
  });

  const refCallback = (node: HTMLCanvasElement) => {
    canvasRef.current = node;
    refs.setReference(node);
  };

  const panes = gutterSize.panes;
  return (
    <>
      {context.open && (
        <SimpleChartTooltip
          ref={refs.setFloating}
          styles={floatingStyles}
          priceHover={priceHover}
          sortConfig={tooltipSortConfig}
          shouldUseLocalDate={isIntradayResample(chartState.resample)}
        />
      )}
      <canvas
        data-testid="chart-area-canvas"
        ref={refCallback}
        style={{ width: options.width, height: options.height }}
      />
      {!!panes.length && axisUnit && displayAxisUnit && (
        <AxisUnit
          top={-10}
          label={axisUnit}
          isMixed={false}
          tooltipLabel=""
          offset={gutterSize.y - gutterSize.yAxisSizes[0].width}
          gutterWidth={gutterSize.yAxisSizes[0].width}
        />
      )}
      {!!insights.length && !insightsLoading && (
        <Insights
          chartApiRef={chartApiRef}
          canvasRef={canvasRef}
          insights={insights}
          eventsConfig={eventsConfig}
          isSimpleChart
          chartWidth={options.width}
          chartHeight={options.height}
          minIconY={options.panePaddingTop}
          minRightOffset={
            (gutterSize.yAxisSizes[0]?.width ?? options.gutters.y) +
            INSIGHT_RIGHT_AXIS_BUFFER_PX
          }
          source={source}
          disableClick={config.app.isTeamsApp}
        />
      )}
    </>
  );
};
